import React, { useCallback, useState } from 'react'
import { InputProps, CheckboxProps, TextAreaProps } from '../../types'
import './input-component.scss'
import { ERROR, SUCCESS } from '../../utils/message'
import { StatusLabel } from '../StatusLabel'
import { CheckCircleIcon, ReportIcon, EyeIcon, SlashedEyeIcon } from '../Icon/icon-component'

const Input = ({
  type,
  labelText,
  name,
  value,
  infoText,
  handleChange,
  handleFocus,
  ref,
  required,
  placeholder,
  errors,
  touched,
  disabled,
  maxValue,
  dirty,
  handleKeyPress,
  keyToTrigger,
  inputMode,
  ...props
}: InputProps) => {
  const isError = errors && errors[name] && touched && touched[name]
  const isTouched = !!(touched && touched[name])
  const isValid = !errors && dirty
  const [hide, setHide] = useState(true)
  const getClassName = useCallback(() => {
    if (isValid) return 'success-input input-field'
    else if (isError) return 'error-input input-field'
    else return 'input-field'
  }, [errors])

  const setType = useCallback(() => {
    if (type !== 'password') {
      return type
    }
    if (type === 'password') {
      return hide ? type : 'text'
    }
  }, [hide, type])

  const getMessageType = useCallback(() => {
    if (isValid) {
      return SUCCESS
    }
    if (isError) {
      return ERROR
    }
    return SUCCESS
  }, [errors])
  const handleIconClick = () => {
    setHide(!hide)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === keyToTrigger) {
      handleKeyPress && handleKeyPress()
    }
  }
  return (
    <>
      {labelText && (
        <div data-testid={labelText} className='input-label'>
          {labelText}
        </div>
      )}
      <div className='input-field-wrapper'>
        <input
          name={name}
          {...props}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          ref={ref}
          className={getClassName()}
          placeholder={placeholder}
          type={setType()}
          required={required}
          disabled={disabled}
          max={maxValue}
          inputMode={inputMode}
          onKeyDown={handleKeyDown}
        />
        <span className='input-icon'>
          {isValid && <CheckCircleIcon height='20' width='20' type={getMessageType()} />}
          {isError && <ReportIcon height='20' width='20' type={getMessageType()} />}
          {hide && !isError && type === 'password' && (
            <EyeIcon type='default-light' height='16' width='20' onClick={handleIconClick} />
          )}
          {!hide && !isError && type === 'password' && (
            <SlashedEyeIcon type='default-light' height='16' width='20' onClick={handleIconClick} />
          )}
        </span>
      </div>
      {(isError || (infoText && isTouched)) && (
        <div className='mt-8'>
          {isError && (
            <StatusLabel type={getMessageType()} variant='basic' text={errors[name] ?? ''} />
          )}
          {!isError && infoText && isTouched && (
            <StatusLabel type='info' variant='basic' text={infoText} />
          )}
        </div>
      )}
    </>
  )
}

const CheckboxInput = ({
  name,
  labelText,
  value,
  handleChange,
  disabled = false,
  dataTestId,
}: CheckboxProps) => {
  return (
    <div className='input-checkbox-container'>
      <input
        className='input-checkbox'
        checked={value === 'true' ? true : false}
        name={name}
        type={'checkbox'}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        data-testid={dataTestId}
      />
      <span className='input-checkbox-label'> {labelText} </span>
    </div>
  )
}

const TextboxInput = ({
  labelText,
  name,
  value,
  handleChange,
  required,
  placeholder,
  errors,
  touched,
  dirty,
  maxLength,
  maxLengthErrorMessage,
}: TextAreaProps) => {
  const isError = errors && errors[name] && touched && touched[name]
  const isValid = !errors && dirty

  // Real-time character limit validation
  const isOverLimit = maxLength ? value.length > maxLength : false
  const hasCharacterError = isOverLimit

  // Combined error state (Yup validation OR character limit)
  const hasAnyError = isError || hasCharacterError

  const getClassName = useCallback(() => {
    if (isValid && !hasCharacterError) return 'success-input input-field'
    else if (hasAnyError) return 'error-input input-field'
    else return 'input-field'
  }, [errors, hasCharacterError, isValid])

  const getMessageType = useCallback(() => {
    if (isValid && !hasCharacterError) {
      return SUCCESS
    }
    if (hasAnyError) {
      return ERROR
    }
    return SUCCESS
  }, [errors, hasCharacterError, isValid])

  return (
    <>
      {labelText && <div className='input-label'> {labelText}</div>}
      <textarea
        placeholder={placeholder}
        required={required}
        className={getClassName()}
        name={name}
        value={value}
        onChange={handleChange}
        maxLength={maxLength ? maxLength + 100 : undefined}
      />
      <span className='input-icon'>
        {isValid && !hasCharacterError && (
          <CheckCircleIcon height='20' width='20' type={getMessageType()} />
        )}
        {hasAnyError && <ReportIcon height='20' width='20' type={getMessageType()} />}
      </span>
      {maxLength && (
        <div className={`character-count ${hasCharacterError ? 'character-count-error' : ''}`}>
          {value.length}/{maxLength}
        </div>
      )}
      {/* Show Yup validation error if field is touched, otherwise show character limit error */}
      {isError && <StatusLabel type={getMessageType()} variant='basic' text={errors[name]} />}
      {!isError && hasCharacterError && (
        <StatusLabel
          type={getMessageType()}
          variant='basic'
          text={maxLengthErrorMessage || `Must not exceed ${maxLength} characters`}
        />
      )}
    </>
  )
}

export { Input, CheckboxInput, TextboxInput }
